import LandingPage from '@/components/layout/landing-page';
import MediaGrid from '@/components/media/grid';
import { usePopularMedia, useRecentMedia } from '@/hooks/api';
import { Category, fetchCategories } from '@/prisma/utils/categories';
import {
	fetchFilteredMedia,
	fetchPopularMedia,
	fetchRecentMedia,
	Media,
} from '@/prisma/utils/media';
import { fetchTags, Tag } from '@/prisma/utils/tag';
import { Heading, Text } from '@chakra-ui/react';
import { GetStaticPathsResult, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { QueryClient } from 'react-query';
import { dehydrate } from 'react-query/hydration';

type MediaIndexPageProps = {
	mediaFilter: string;
	mediaFilterTags: Tag[];
	mediaFilterCategories: Category[];
	mediaForTags: Media[];
	mediaForCategories: Media[];
};

const MediaIndexPage: React.FC<MediaIndexPageProps> = (
	props: MediaIndexPageProps
) => {
	const router = useRouter();
	const { data: popularMedia } = usePopularMedia();
	const { data: recentMedia } = useRecentMedia();

	// If the page is not yet generated, this will be displayed
	// initially until getStaticProps() finishes running
	if (router.isFallback) {
		return <div>Loading...</div>;
	}

	return (
		<LandingPage
			title={'Videos | Yoga!'}
			description="Best Yoga videos online!"
		>
			{props.mediaForCategories.length > 0 && (
				<>
					<Heading size="lg">Category videos</Heading>
					<Text>
						Videos for{' '}
						{props.mediaFilterCategories.map((c) => c.name).join(', ')}.
					</Text>
					<MediaGrid media={props.mediaForCategories} />
				</>
			)}

			{props.mediaForTags.length > 0 && (
				<>
					<Heading size="lg">Tagged videos</Heading>
					<Text>
						Videos for {props.mediaFilterTags.map((c) => c.name).join(', ')}.
					</Text>
					<MediaGrid media={props.mediaForTags} />
				</>
			)}

			<Heading size="lg">Popular videos</Heading>
			<Text></Text>
			<MediaGrid media={popularMedia || []} />

			<Heading size="lg">Recent videos</Heading>
			<Text></Text>
			<MediaGrid media={recentMedia || []} />
		</LandingPage>
	);
};

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
	const paths = ['tag/tasty'].map((p) => ({
		params: { uri: [p] },
	}));

	// We'll pre-render only these paths at build time.
	// { fallback: blocking } will server-render pages
	// on-demand if the path doesn't exist.
	return {
		paths: [],
		fallback: true,
		// fallback: 'blocking',
	};
}

export const getStaticProps: GetStaticProps = async ({ params, ...rest }) => {
	const queryClient = new QueryClient();

	await queryClient.prefetchQuery(['media', { popular: true }], () =>
		fetchPopularMedia()
	);
	await queryClient.prefetchQuery(['media', { recent: true }], () =>
		fetchRecentMedia()
	);

	// filter media by tags and cats
	let tags: Tag[] = [];
	let categories: Category[] = [];
	let mediaForTags: Media[] = [];
	let mediaForCategories: Media[] = [];

	const { uri } = params as { uri: string[] | undefined };
	const [filter, ...slugs] = uri || [];

	switch (filter) {
		case 'tags': {
			tags = await fetchTags({
				where: {
					slug: {
						in: slugs,
					},
				},
			});

			mediaForTags = await fetchFilteredMedia({
				tags: slugs,
			});

			break;
		}

		case 'categories': {
			categories = await fetchCategories({
				where: {
					slug: {
						in: slugs,
					},
				},
			});

			mediaForCategories = await fetchFilteredMedia({
				categories: slugs,
			});

			break;
		}
	}

	return {
		revalidate: 5,
		props: {
			dehydratedState: dehydrate(queryClient),
			mediaFilter: filter || null,
			mediaFilterTags: tags,
			mediaFilterCategories: categories,
			mediaForTags,
			mediaForCategories,
		},
	};
};

export default MediaIndexPage;
